import moment from "moment-timezone";

/**
 * Client-side helpers for the Past Performance heatmap's month drill-down.
 * Kept out of `performance-utils` because that module is server-only (knex).
 *
 * A "month id" is the key the heatmap grid is built on and the shape the page
 * stores its month selection in, e.g. "Sep-2026".
 */
export const MONTH_ID_FORMAT = "MMM-YYYY";

const API_DATE_FORMAT = "YYYY-MM-DD";

/** The month id a moment falls in. */
export const toMonthId = (date: moment.Moment) => date.format(MONTH_ID_FORMAT);

/** The API date string for one day of a month id, e.g. (12, "Sep-2026"). */
export const toMonthDayDate = (day: number, monthId: string) =>
  moment(`${day}-${monthId}`, `D-${MONTH_ID_FORMAT}`).format(API_DATE_FORMAT);

/** First and last day of a month id; null when the id cannot be parsed. */
export const monthBounds = (monthId: string) => {
  const month = moment(monthId, MONTH_ID_FORMAT, true);
  if (!month.isValid()) return null;
  return {
    start: month.clone().startOf("month"),
    end: month.clone().endOf("month"),
  };
};

/**
 * The slice of a month that falls inside [fromDate, toDate] — so a drill-down
 * can never query outside the window the date-range picker allows. Null when
 * the month lies entirely outside the range (or the id is unparseable).
 */
export const clampMonthToRange = (
  monthId: string,
  fromDate: string,
  toDate: string,
) => {
  const bounds = monthBounds(monthId);
  if (!bounds) return null;

  const start = moment.max(bounds.start, moment(fromDate));
  const end = moment.min(bounds.end, moment(toDate));
  if (start.isAfter(end)) return null;

  return {
    fromDate: start.format(API_DATE_FORMAT),
    toDate: end.format(API_DATE_FORMAT),
  };
};

/** Whether any day of the month still falls inside the range. */
export const monthIntersectsRange = (
  monthId: string,
  fromDate: string,
  toDate: string,
) => clampMonthToRange(monthId, fromDate, toDate) !== null;

/** Whether a single day still falls inside the range, both ends inclusive. */
export const isDateInRange = (date: string, fromDate: string, toDate: string) =>
  moment(date).isBetween(fromDate, toDate, "day", "[]");

/** Human-readable label for a drill-down selection, e.g. "12 Sep 2026". */
export const formatSelectionLabel = (
  heatmapDate?: string | null,
  monthId?: string | null,
) => {
  if (heatmapDate) return moment(heatmapDate).format("DD MMM YYYY");
  if (monthId) {
    const bounds = monthBounds(monthId);
    if (bounds) return bounds.start.format("MMMM YYYY");
  }
  return null;
};
